<template>
    <information-modal
        :title="vendorAgencyTitle"
        v-model="isShowVendorAgency">
        <b-grid
            caption="Vendors agenices"
            :tableOptions="vendorOriStore.tableOptions"
            :filterableFields="vendorOriStore.filterableFields"
            :filterOptions="vendorOriStore.filterOptions"
            @optionChange="getAgenciesData"
            :items="vendorOriStore.items"
            :columns="columnsVendorAgency"
            :loading="loading">
        </b-grid>
    </information-modal>

    <form-modal
        :title="editDialogTitle"
        v-model="isShowEditEditor"
        :loading="loading"
        @submit="createVendor">
        <fieldset>
            <div class="mb-3 row">
                <label
                    for="vendorName"
                    class="col-sm-3 col-form-label">
                    Vendor Name:
                </label>
                <div class="col-sm-9">
                    <b-input
                        name="vendorName"
                        type="text"
                        :max-length="100"
                        v-model="vendor.name"
                        :error="errors.name" />
                </div>
            </div>           
        </fieldset>
    </form-modal>
    <b-grid
        caption="Vendors"
        :tableOptions="vendorStore.tableOptions"
        :filterableFields="vendorStore.filterableFields"
        :filterOptions="vendorStore.filterOptions"
        @optionChange="getData"
        :items="vendorStore.items"
        :columns="columns"
        :loading="loading">
        <template #beforeFilter>
            <b-button
                @click.prevent="showEditEditor('')"
                v-if="canAdd"
                >Add New Vendor</b-button
            >
        </template>

        <template #cell.agencyCount="{item}">
            <button
                class="btn btn-link p-0"
                v-if="item.agencyCount > 0"
                @click.prevent="showVendorAgency(item)"
                :aria-label="`View ${item.agencyCount} ${item.agencyCount === 1 ? 'agency' : 'agencies'} for vendor ${item.name}`">
                {{ item.agencyCount }}
            </button>
            <span v-else>{{ item.agencyCount }}</span>
        </template>

        <template #cell.actions="{item}">
            <div class="button-wrapper">
                <b-button
                    @click.prevent="showEditEditor(item.code)"
                    v-if="canEdit"
                    :aria-label="`Edit vendor ${item.name}`"
                    size="sm">
                    Edit
                </b-button>
                <b-button
                    @click.prevent="deleteItem(item.code)"
                    :disabled="!canDelete(item)"
                    :aria-label="`Delete vendor ${item.name}. ${deleteButtonTooltip(item)}`"
                    v-tooltip="deleteButtonTooltip(item)"
                    size="sm"
                    variant="danger">
                    Delete
                </b-button>
            </div>
        </template>
        <template #pagingStart>
            <div
                class="btn-group"
                role="group">
                <drop-down
                    label="Export Data"
                    :item-count="2"
                    :is-primary-button="true">
                    <template v-slot:item1="{item}">
                        <button
                            class="dropdown-item"
                            @click.prevent="exportData('csv')">
                            Download as .csv
                        </button>
                    </template>
                    <template v-slot:item2="{item}">
                        <button
                            class="dropdown-item"
                            @click.prevent="exportData('xlsx')">
                            Download as .xlsx
                        </button>
                    </template>
                </drop-down>
            </div>
        </template>
    </b-grid>

    <system-alerts></system-alerts>
    <confirm-modal></confirm-modal>
</template>
<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {BTableColumn} from "./BTable/BTable.types";
import {FormModal, InformationModal, SystemAlerts, ConfirmModal} from "./Misc";
import BGrid from "./BGrid/BGrid.vue";
import type {Vendor} from "@/types";
import {useFormValidation} from "@/validation";
import {object, string} from "yup";
import {BInput} from "./Fields";
import {BButton} from "./BButton";
import * as Yup from "yup";
import DropDown from "@/components/Misc/DropDown.vue";
import {saveFile} from "@/composable/filesHelper";
import {useConfirmDialog} from "@/composable/useConfirmDialog";
import {storeToRefs} from "pinia";
import {useVendorStore} from "@/stores/vendorStore";
import {useVendorOriStore} from "@/stores/vendorOriStore";

const isShowEditEditor = ref(false);
const vendorStore = useVendorStore();
const vendorOriStore = useVendorOriStore();
const {confirm} = useConfirmDialog();
const {loading} = storeToRefs(vendorStore);
const vendorAgencyTitle = ref("");
const isShowVendorAgency = ref(false);

const columns = ref<BTableColumn[]>([
    {
        key: "name",
        text: "Vendor name",
        sortable: true,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.name,
    },

    {
        key: "agencyCount",
        text: "Number of Agencies",
        sortable: true,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.agencyCount,
    },

    {
        key: "actions",
        text: "",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap text-end"],
        value: (item) => item["link"],
    },
]);

const columnsVendorAgency = ref<BTableColumn[]>([
    {
        key: "ori",
        text: "ORI",
        sortable: true,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.ori,
    },

    {
        key: "oriName",
        text: "Agency Name",
        sortable: true,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.oriName,
    },
    {
        key: "dataCollection",
        text: "Data Collection(s)",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.collections,
    },
]);

const canAdd = computed(() => {
    return true;
});
const canDelete = (item: Vendor) => {
    return item.agencyCount == 0 && item.code != "UV";
};
const deleteButtonTooltip = (item: Vendor) => {
    if (item.code == "UV") {
        return "Cannot delete: this is the default vendor";
    }
    if (item.agencyCount > 0) {
        return `Cannot delete: vendor is linked to ${item.agencyCount} ${item.agencyCount == 1 ? "agency" : "agencies"}`;
    }
    return "Delete vendor";
};
const canEdit = computed(() => {
    return true;
});

const editDialogTitle = computed(() => {
    return selectedVendorId.value ? `Edit vendor - ${vendor.value.name}`:"Add new vendor";
});

const canEditCode = computed(() => {
    return !selectedVendorId.value;
});

const vendor = ref<Vendor>({} as Vendor);
const selectedVendorId = ref<string>("");

Yup.addMethod(Yup.string, 'uniqueVendorName', function (errorMessage: string) {
    return this.test('unique-vendor-name', errorMessage, async function (value) {
        const { path, createError } = this;

        var vendorAlreadyExists = value != undefined ? (await vendorStore.checkIfVendorNameExists(value)): true;
        return vendorAlreadyExists 
            ? createError({ path, message: errorMessage }) 
            : true;
    });
});

const schema = object<Vendor>({    
    name: string().nullable().trim().label("Vendor name").max(100).required().uniqueVendorName("Vendor Name already exists!"),
});

const createVendor = async () => {
    if (await handleSubmit(schema, vendor.value)) {
        if (selectedVendorId.value == "") {
            await vendorStore.create(vendor.value);
        } else {
            await vendorStore.update(selectedVendorId.value, vendor.value);
        }

        isShowEditEditor.value = false;
        await getData();
    }
};

const exportData = async (type: string) => {
    let blob = await vendorStore.exportFile(type);
    saveFile(`vendors.${type}`, blob);
};

const deleteItem = async (code: string) => {
    const shouldProceed = await confirm("Delete Confirmation", "Are You Sure?");
    if (shouldProceed) {
        await vendorStore.deleteItem(code);
        await getData();
    }
};

const showVendorAgency = async (vendor: Vendor) => {
    vendorOriStore.reset();
    isShowVendorAgency.value = true;
    vendorAgencyTitle.value = `Agencies for ${vendor.name}`;
    selectedVendorId.value = vendor.code;
    getAgenciesData();
};

const showEditEditor = async (vendorId: string) => {
    selectedVendorId.value = vendorId;
    resetValidation();

    if (vendorId == "") {
        vendor.value = {} as Vendor;
    } else {
        vendor.value = await vendorStore.getById(vendorId);
    }
    isShowEditEditor.value = true;
};

const getData = async () => {
    await vendorStore.list();
};

const getAgenciesData = async () => {
    await vendorOriStore.list(selectedVendorId.value);
};

const {handleSubmit, errors, resetValidation} = useFormValidation();

onMounted(async () => {
    await getData();
});
</script>
<style>
.agency-access {
    font-size: 0.9rem;
}
</style>
